import { Box, Stack } from '@presov.sk/presov-ui-react';
import React from 'react';

/** Výška zatvoreného listu - vojde sa doň presne prepínač segmentu. */
export const SHEET_COLLAPSED = 184;
/** Vytiahnutý list. Percento, nie pixely: mapa nad ním musí zostať čitateľná. */
export const SHEET_EXPANDED = '76%';

type Props = {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  children?: React.ReactNode;
  handleLabel?: string;
};

/**
 * Spodný list so zoznamom na mobile. Nahradil prepínač Zoznam/Mapa: mapa aj
 * zoznam sú vidieť naraz a prepína sa medzi nimi gestom, nie tlačidlom.
 *
 * **Nie je to `BottomSheet` z knižnice** a nemôže ním byť: ten je modál
 * v portáli so zatmavením, teda mapa pod ním by bola neovládateľná a stále
 * stmavená. Tento list je súčasťou stránky, nič nezatmieva a mapa pod ním
 * funguje ďalej.
 */
const FeedSheet: React.FC<Props> = ({
  expanded,
  onExpandedChange,
  children,
  handleLabel = 'Zoznam',
}) => {
  const touchStartRef = React.useRef<number | null>(null);

  /**
   * `delta` je posun **obsahu**: záporný znamená ťah nahor, teda vytiahnutie,
   * kladný ťah nadol, teda zavretie. Zavrieť sa dá len vtedy, keď je zoznam
   * odrolovaný hore - inak by sa list zavrel pri prvom pokuse skrolovať späť
   * na začiatok.
   */
  const pull = React.useCallback(
    (delta: number, atTop: boolean) => {
      if (delta < 0 && !expanded) {
        onExpandedChange(true);
        return;
      }
      if (delta > 0 && expanded && atTop) {
        onExpandedChange(false);
      }
    },
    [expanded, onExpandedChange],
  );

  const isAtTop = (target: EventTarget | null) => {
    const scroller = (target as HTMLElement | null)?.closest?.(
      '[data-feed-scroll]',
    );
    return !scroller || scroller.scrollTop <= 0;
  };

  return (
    <Box
      className='screen-only'
      // List sa zatvára ťahom nadol, a to práve vtedy, keď je zoznam
      // odrolovaný hore - teda presne keď by sa spustilo obnovenie stránky
      // (`Pulldown`). Bez tejto značky zatvorenie listu načítalo celú stránku.
      data-pulldown-blocked='true'
      // Znamienko sa obracia zámerne. Dotyk hlási posun prsta (`hore` je
      // záporné), koliesko hlási posun obsahu - a pri prirodzenom skrolovaní,
      // čo je predvolené na trackpade aj na dotykových obrazovkách, dávajú dva
      // prsty smerom hore `deltaY > 0`. Bez otočenia by to isté gesto list
      // podľa vstupného zariadenia raz otváralo a raz zatváralo.
      onWheel={(event) => pull(-event.deltaY, isAtTop(event.target))}
      onTouchStart={(event) => {
        touchStartRef.current = event.touches[0]?.clientY ?? null;
      }}
      onTouchMove={(event) => {
        const start = touchStartRef.current;
        const current = event.touches[0]?.clientY;
        if (start === null || current === undefined) return;
        const delta = current - start;
        // Krátke pohyby sú skrolovanie zoznamu, nie gesto na celý list.
        if (Math.abs(delta) < 24) return;
        touchStartRef.current = current;
        pull(delta, isAtTop(event.target));
      }}
      sx={(theme) => ({
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2,
        height: expanded ? SHEET_EXPANDED : SHEET_COLLAPSED,
        // Rovnaký prechod má aj tlačidlo nového podnetu, aby sa hýbali spolu.
        transition: 'height .26s ease',
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(1, 1.5, 1.5),
        backgroundColor: theme.palette.background.paper,
        borderRadius: `${theme.typography.pxToRem(
          20,
        )} ${theme.typography.pxToRem(20)} 0 0`,
        boxShadow: theme.overlayShadow.sheet,
      })}
    >
      <Stack
        component='button'
        type='button'
        aria-expanded={expanded}
        aria-label={handleLabel}
        onClick={() => onExpandedChange(!expanded)}
        sx={{
          flex: 'none',
          alignItems: 'center',
          padding: 0,
          paddingBottom: 1,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
        }}
      >
        {/* Úchop. Ťahá sa celý list, ale bez tejto čiarky to na ňom nie je
            vidieť - a zároveň je to klikacia náhrada gesta. */}
        <Box
          sx={(theme) => ({
            width: 44,
            height: 4,
            borderRadius: `${theme.shape.borderRadiusFull}px`,
            backgroundColor: theme.palette.grey[300],
          })}
        />
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Box>
  );
};

export default FeedSheet;
